import React, { useCallback, useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Row,
  Space,
  Statistic,
  Typography,
  message,
} from "antd";
import {
  DollarCircleOutlined,
  ShoppingOutlined,
  BuildOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import {
  getMonthlyOverview,
  getMonthlyOverviewDetails,
} from "../../api/monthlyOverviewApi";
import {
  MonthlyOverviewMetricKey,
  MonthlyOverviewResponse,
} from "../../types/monthlyOverview.type";
import MonthlyOverviewDetailsModal from "./monthlyOverviewDetailsModal";
import { exportFullOverview } from "./monthlyOverviewExport";

const { Title, Text } = Typography;

/**
 * Конфигурация карточек-показателей. Чтобы добавить новую метрику
 * (прибыль, маржа, задолженность клиентов и т.д.) — достаточно добавить
 * сюда запись и соответствующее поле в MonthlyOverviewMetrics.
 */
const METRIC_CARDS: Array<{
  key: MonthlyOverviewMetricKey;
  title: string;
  color: string;
  icon: React.ReactNode;
}> = [
  {
    key: "receivedFromClients",
    title: "Получено от клиентов",
    color: "#3f8600",
    icon: <DollarCircleOutlined />,
  },
  {
    key: "producedGoods",
    title: "Произведено товаров",
    color: "#1677ff",
    icon: <BuildOutlined />,
  },
  {
    key: "sales",
    title: "Продажи",
    color: "#722ed1",
    icon: <ShoppingOutlined />,
  },
];

export default function MonthlyOverview() {
  const [selectedMonth, setSelectedMonth] = useState<Dayjs>(dayjs());
  const [data, setData] = useState<MonthlyOverviewResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeMetric, setActiveMetric] =
    useState<MonthlyOverviewMetricKey | null>(null);
  const [exporting, setExporting] = useState<boolean>(false);

  const fetchData = useCallback(async (month: Dayjs) => {
    setLoading(true);
    try {
      const result = await getMonthlyOverview(month.month() + 1, month.year());
      setData(result);
    } catch (error) {
      console.error("Ошибка при загрузке обзора месяца:", error);
      message.error("Не удалось загрузить обзор месяца");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(selectedMonth);
  }, [selectedMonth, fetchData]);

  const activeCard = METRIC_CARDS.find((c) => c.key === activeMetric);

  const handleExportAll = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const month = selectedMonth.month() + 1;
      const year = selectedMonth.year();
      // Подтягиваем расшифровки по всем метрикам и собираем единую книгу.
      const detailsList = await Promise.all(
        METRIC_CARDS.map((card) =>
          getMonthlyOverviewDetails(card.key, month, year)
        )
      );
      exportFullOverview(data, detailsList);
    } catch (error) {
      console.error("Ошибка при экспорте обзора месяца:", error);
      message.error("Не удалось экспортировать обзор");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>Обзор месяца</Title>

      <Space style={{ marginBottom: 24 }} wrap>
        <DatePicker
          picker="month"
          allowClear={false}
          value={selectedMonth}
          onChange={(value) => value && setSelectedMonth(value)}
          format="MMMM YYYY"
        />
        <Button
          type="primary"
          icon={<FileExcelOutlined />}
          loading={exporting}
          disabled={loading || !data}
          onClick={handleExportAll}
        >
          Экспорт в Excel
        </Button>
      </Space>

      <Row gutter={[16, 16]}>
        {METRIC_CARDS.map((card) => (
          <Col xs={24} sm={12} lg={8} key={card.key}>
            <Card
              hoverable
              onClick={() => setActiveMetric(card.key)}
              style={{ cursor: "pointer" }}
            >
              <Statistic
                title={card.title}
                value={data ? data.metrics[card.key] : 0}
                precision={0}
                groupSeparator=" "
                suffix="UZS"
                loading={loading}
                valueStyle={{ color: card.color }}
                prefix={card.icon}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Нажмите, чтобы посмотреть расшифровку
              </Text>
            </Card>
          </Col>
        ))}
      </Row>

      <MonthlyOverviewDetailsModal
        open={activeMetric !== null}
        metric={activeMetric}
        title={activeCard?.title ?? ""}
        month={selectedMonth.month() + 1}
        year={selectedMonth.year()}
        onClose={() => setActiveMetric(null)}
      />
    </div>
  );
}
